import { buttonClass } from '@mogzo/ui';
import { Search } from 'lucide-react';
import { createElement, type ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { MemoryRouter, useNavigate } from 'react-router';
import { DOC_PAGES } from './pages';

/** One thing search can find: a page, or one of its headings with the text under it. */
export interface SearchEntry {
  slug: string;
  page: string;
  group: string;
  heading?: { id: string; text: string };
  text: string;
}

/**
 * The docs' text, read from the pages themselves (rendered once, off screen), so search never
 * goes out of date. Loaded the first time someone searches.
 */
export async function buildIndex(): Promise<SearchEntry[]> {
  const { renderToStaticMarkup } = await import('react-dom/server');
  const entries: SearchEntry[] = [];
  for (const page of DOC_PAGES) {
    const html = renderToStaticMarkup(
      createElement(MemoryRouter, null, createElement(page.Content)),
    );
    const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    if (!root) continue;
    // Table cells and list items read as separate words.
    for (const cell of root.querySelectorAll('td, th, li, dt, dd, p')) cell.append(' ');
    let section: SearchEntry = { slug: page.slug, page: page.title, group: page.group, text: '' };
    entries.push(section);
    for (const node of [...root.children]) {
      if (node.tagName === 'H2' && node.id) {
        section = {
          slug: page.slug,
          page: page.title,
          group: page.group,
          heading: { id: node.id, text: node.textContent ?? '' },
          text: '',
        };
        entries.push(section);
      } else if (node.tagName !== 'H1') {
        section.text += ` ${node.textContent ?? ''}`;
      }
    }
  }
  return entries.map((entry) => ({ ...entry, text: entry.text.replace(/\s+/g, ' ').trim() }));
}

/** Entries that hold every word of the query, best first: page titles, then headings, then text. */
export function searchDocs(entries: SearchEntry[], query: string, limit = 12): SearchEntry[] {
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  return entries
    .map((entry) => {
      const title = entry.page.toLowerCase();
      const heading = entry.heading?.text.toLowerCase() ?? '';
      const text = entry.text.toLowerCase();
      let score = 0;
      for (const word of words) {
        if (!entry.heading && title.includes(word)) score += 10;
        else if (heading.includes(word)) score += 5;
        else if (text.includes(word) || title.includes(word)) score += 1;
        else return null;
      }
      return { entry, score };
    })
    .filter((found): found is { entry: SearchEntry; score: number } => found !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((found) => found.entry);
}

/** About 120 characters of `text` around the first word found, with the words marked. */
function snippet(text: string, words: string[]): ReactNode[] {
  const lower = text.toLowerCase();
  const at = Math.max(0, Math.min(...words.map((w) => lower.indexOf(w)).filter((i) => i >= 0)));
  const start = Math.max(0, at - 40);
  const piece = `${start > 0 ? '…' : ''}${text.slice(start, start + 120)}${text.length > start + 120 ? '…' : ''}`;
  return mark(piece, words);
}

function mark(text: string, words: string[]): ReactNode[] {
  if (!words.length) return [text];
  const escaped = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  return text.split(new RegExp(`(${escaped.join('|')})`, 'gi')).map((part, i) =>
    i % 2 ? (
      // biome-ignore lint/suspicious/noArrayIndexKey: pieces of one line of text
      <mark key={i}>{part}</mark>
    ) : (
      part
    ),
  );
}

const isMac = () => /Mac|iPhone|iPad/.test(navigator.platform);

/** The Search docs button in the top bar, and its dialog (Ctrl K, or ⌘ K on a Mac). */
export function DocsSearch() {
  const dialog = useRef<HTMLDialogElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [entries, setEntries] = useState<SearchEntry[] | null>(null);
  const [selected, setSelected] = useState(0);
  const results = useMemo(() => (entries ? searchDocs(entries, query) : []), [entries, query]);
  const words = query.toLowerCase().split(/\s+/).filter(Boolean);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    const element = dialog.current;
    if (!element) return;
    if (open) {
      if (!element.open) element.showModal?.();
      input.current?.focus();
      if (!entries) buildIndex().then(setEntries, () => setEntries([]));
    } else if (element.open) {
      element.close?.();
    }
  }, [open, entries]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: back to the top for each query
  useEffect(() => setSelected(0), [query]);

  function go(entry: SearchEntry) {
    setOpen(false);
    setQuery('');
    const base = entry.slug === 'introduction' ? '/docs' : `/docs/${entry.slug}`;
    navigate(entry.heading ? `${base}#${entry.heading.id}` : base);
  }

  function onKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setSelected((n) => Math.min(n + 1, results.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setSelected((n) => Math.max(n - 1, 0));
    } else if (event.key === 'Enter' && results[selected]) {
      event.preventDefault();
      go(results[selected]);
    }
  }

  const shortcut = isMac() ? '⌘ K' : 'Ctrl K';
  return (
    <>
      <button
        type="button"
        className={`${buttonClass('secondary', 'sm')} w-[220px] max-w-[40vw] justify-start text-muted`}
        onClick={() => setOpen(true)}
        aria-keyshortcuts="Control+K Meta+K"
      >
        <Search size={14} strokeWidth={1.75} aria-hidden="true" />
        <span className="flex-1 text-left">Search docs</span>
        <span className="mz-kbd max-sm:hidden">{shortcut}</span>
      </button>
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape closes a dialog already; the click is for the backdrop */}
      <dialog
        ref={dialog}
        className="mz-search"
        aria-label="Search docs"
        onClose={() => setOpen(false)}
        onCancel={() => setOpen(false)}
        onClick={(event) => {
          // A click on the backdrop closes it.
          if (event.target === dialog.current) setOpen(false);
        }}
      >
        {open && (
          <>
            <div className="mz-search-input">
              <Search size={18} strokeWidth={1.75} aria-hidden="true" />
              <label htmlFor="docs-search" className="mz-sr-only">
                Search docs
              </label>
              <input
                ref={input}
                id="docs-search"
                type="search"
                autoComplete="off"
                placeholder="Saves, leaderboards, mogzo upload…"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={onKeyDown}
                role="combobox"
                aria-expanded={results.length > 0}
                aria-controls="docs-search-results"
                aria-activedescendant={results[selected] ? `docs-result-${selected}` : undefined}
              />
              <span className="mz-kbd">Esc</span>
            </div>
            <div
              id="docs-search-results"
              className="mz-search-results"
              role="listbox"
              aria-label="Results"
            >
              {query && entries && results.length === 0 && (
                <p className="m-0 p-3 text-muted">Nothing in the docs matches “{query}”.</p>
              )}
              {query && !entries && <p className="m-0 p-3 text-muted">Reading the docs…</p>}
              {results.map((entry, i) => (
                <a
                  key={`${entry.slug}-${entry.heading?.id ?? ''}`}
                  id={`docs-result-${i}`}
                  role="option"
                  aria-selected={i === selected}
                  href={`/docs/${entry.slug}${entry.heading ? `#${entry.heading.id}` : ''}`}
                  onClick={(event) => {
                    event.preventDefault();
                    go(entry);
                  }}
                  onMouseEnter={() => setSelected(i)}
                >
                  <span className="font-medium">
                    {mark(entry.heading ? entry.heading.text : entry.page, words)}
                  </span>
                  <span className="mz-search-where">
                    {entry.heading ? entry.page : entry.group}
                    {entry.text && ' · '}
                    {entry.text && snippet(entry.text, words)}
                  </span>
                </a>
              ))}
            </div>
            <div className="mz-search-foot">
              <span>
                <span className="mz-kbd">↑</span> <span className="mz-kbd">↓</span> to move
              </span>
              <span>
                <span className="mz-kbd">Enter</span> to open
              </span>
              <span>
                <span className="mz-kbd">Esc</span> to close
              </span>
            </div>
          </>
        )}
      </dialog>
    </>
  );
}
